import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import { useTermsAndConditions } from '../hooks/useTermsAndConditions';
import { useTermsAndConditionsStore } from '../stores/termsAndConditionsStore';
import { TermsAndConditionsForm } from '../components/TermsAndConditionsForm';
import { TermsAndConditionsDTO } from '../types';

const { Title } = Typography;

export const FormView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  const { currentItem, setCurrentItem } = useTermsAndConditionsStore();
  const { 
    getById, 
    termsById, 
    loading: hookLoading, 
    createTermsAndConditions, 
    updateTermsAndConditions 
  } = useTermsAndConditions();
  
  const [submitting, setSubmitting] = useState(false);
  const loading = hookLoading || submitting;

  // Fetch data when component mounts or id changes
  useEffect(() => {
    if (isEditing) {
      if (currentItem && currentItem.id === id) {
        // We already have the data in the store
      } else if (id) {
        // Fetch from API
        getById(id);
      }
    }
  }, [id, isEditing, currentItem, getById]);

  // Update store when API data changes
  useEffect(() => {
    if (termsById) {
      setCurrentItem(termsById);
    }
  }, [termsById, setCurrentItem]);

  // Use either store data or API data
  const initialValues = isEditing ? (currentItem?.id === id ? currentItem : termsById) : undefined;

  const handleSubmit = async (values: TermsAndConditionsDTO) => {
    try {
      setSubmitting(true);
      
      let success;
      if (isEditing && id) {
        success = await updateTermsAndConditions({
          ...values,
          id: id,
        });
        if (success) {
          message.success('Terms and conditions updated successfully');
          navigate(`/terms-and-conditions/${id}`);
        }
      } else {
        success = await createTermsAndConditions(values);
        if (success) {
          message.success('Terms and conditions created successfully');
          navigate('/terms-and-conditions');
        }
      }
    } catch (error) {
      message.error(isEditing ? 'Failed to update terms and conditions' : 'Failed to create terms and conditions');
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isEditing && id) {
      navigate(`/terms-and-conditions/${id}`);
    } else {
      navigate('/terms-and-conditions');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center mb-4">
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleCancel}
          className="mr-4"
        >
          Back
        </Button>
        <Title level={3} className="m-0">
          {isEditing ? 'Edit Terms and Conditions' : 'Create New Terms and Conditions'}
        </Title>
      </div>

      <TermsAndConditionsForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={loading}
      />
    </div>
  );
};
